import { useRef, useState } from 'react';

import Color from 'color';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ColorPickerHue,
  ColorPickerSelection,
  ColorPicker as ShadcnColorPicker,
} from '@/components/ui/shadcn-io/color-picker';
import { DEFAULT_COLOR_VALUES, generateRandomColor } from '@/lib/constants/colors';
import { cn } from '@/lib/utils';

interface ColorPickerProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  colors?: string[];
  defaultColor?: string;
  disabled?: boolean;
}

interface ColorUpdateParams {
  color: string | number[];
  defaultColor: string;
  onChange: (hex: string) => void;
  onUpdateStart: () => void;
  onUpdateEnd: () => void;
}

/**
 * Converts color from various formats to hex string.
 * Supports both RGB arrays from color picker and hex strings from preset colors.
 */
const convertColorToHex = (color: string | number[], defaultColor: string): string => {
  try {
    if (Array.isArray(color)) {
      // Convert RGB array from color picker widget to hex
      const [r, g, b] = color;
      return Color.rgb(r, g, b).hex();
    } else {
      // color is string at this point
      return color;
    }
  } catch (error) {
    // Silently fallback to default color if conversion fails
    return defaultColor;
  }
};

/**
 * Handles color conversion from various formats to hex and manages update state.
 * Supports both RGB arrays from color picker and hex strings from preset colors.
 * Uses setTimeout to prevent rapid successive updates that could cause UI flicker.
 */
const updateColor = ({
  color,
  defaultColor,
  onChange,
  onUpdateStart,
  onUpdateEnd,
}: ColorUpdateParams): void => {
  const hex = convertColorToHex(color, defaultColor);

  onUpdateStart();
  onChange(hex);
  setTimeout(onUpdateEnd, 50);
};

/**
 * A comprehensive color picker component integrated with React Hook Form.
 * Provides multiple color selection methods with form validation support.
 *
 * **Key Features:**
 * - React Hook Form integration with full validation support
 * - Multiple color selection methods: visual picker, preset colors, random generation
 * - Supports both RGB arrays and hex strings with automatic conversion
 * - Prevents recursive updates and UI flicker during rapid changes
 * - Accessible design with proper keyboard navigation
 * - Internationalization support for UI text
 * - Disabled state handling with appropriate visual feedback
 * - Custom preset color palettes support
 *
 * **Color Selection Methods:**
 * 1. **Visual Color Picker**: Interactive HSV color wheel with hue slider
 * 2. **Preset Colors**: Grid of predefined color swatches
 * 3. **Random Generator**: Generates aesthetically pleasing random colors
 *
 * **Technical Implementation:**
 * - Uses debounced updates to prevent UI flicker
 * - Automatically resets internal picker state when presets are selected
 * - Handles color format conversion between RGB arrays and hex strings
 * - Prevents infinite update loops with ref-based state management
 *
 * @example
 * ```tsx
 * // Basic usage in React Hook Form
 * function ProjectForm() {
 *   const form = useForm<{ projectColor: string }>();
 *
 *   return (
 *     <Form {...form}>
 *       <ColorPicker
 *         name="projectColor"
 *         control={form.control}
 *         label="Project Color"
 *       />
 *     </Form>
 *   );
 * }
 *
 * // With custom preset colors
 * const BRAND_COLORS = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'];
 *
 * <ColorPicker
 *   name="brandColor"
 *   control={form.control}
 *   label="Brand Color"
 *   colors={BRAND_COLORS}
 *   defaultColor="#ff6b6b"
 * />
 *
 * // Disabled state
 * <ColorPicker
 *   name="readOnlyColor"
 *   control={form.control}
 *   label="Theme Color"
 *   disabled={!user.canEditColors}
 * />
 *
 * // Without label (compact mode)
 * <ColorPicker
 *   name="accentColor"
 *   control={form.control}
 * />
 *
 * // Complete form integration example
 * function CreateProjectForm() {
 *   const form = useForm<{
 *     name: string;
 *     color: string;
 *   }>({
 *     defaultValues: {
 *       name: '',
 *       color: generateRandomColor()
 *     }
 *   });
 *
 *   const onSubmit = (data: { name: string; color: string }) => {
 *     createProject({
 *       name: data.name,
 *       color: data.color, // Will be a valid hex color string
 *     });
 *   };
 *
 *   return (
 *     <Form {...form}>
 *       <form onSubmit={form.handleSubmit(onSubmit)}>
 *         <FormField
 *           control={form.control}
 *           name="name"
 *           render={({ field }) => (
 *             <FormItem>
 *               <FormLabel>Project Name</FormLabel>
 *               <FormControl>
 *                 <Input {...field} />
 *               </FormControl>
 *             </FormItem>
 *           )}
 *         />
 *
 *         <ColorPicker
 *           name="color"
 *           control={form.control}
 *           label="Project Color"
 *           defaultColor="#4ecdc4"
 *         />
 *
 *         <Button type="submit">Create Project</Button>
 *       </form>
 *     </Form>
 *   );
 * }
 *
 * // Advanced usage with validation
 * const formSchema = z.object({
 *   projectColor: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid hex color')
 * });
 *
 * function ValidatedColorForm() {
 *   const form = useForm<z.infer<typeof formSchema>>({
 *     resolver: zodResolver(formSchema)
 *   });
 *
 *   return (
 *     <ColorPicker
 *       name="projectColor"
 *       control={form.control}
 *       label="Project Color"
 *       // Component automatically outputs valid hex strings
 *     />
 *   );
 * }
 * ```
 */
export function ColorPicker<T extends FieldValues>({
  name,
  control,
  label,
  colors = DEFAULT_COLOR_VALUES,
  defaultColor = generateRandomColor(),
  disabled = false,
}: ColorPickerProps<T>) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  // Prevents recursive updates when color picker internally triggers onChange
  const isUpdatingRef = useRef(false);
  // Forces color picker widget to reset its internal state when preset colors are selected
  const [resetKey, setResetKey] = useState(0);
  // Track the last color value to prevent duplicate onChange calls
  const lastColorRef = useRef<string | null>(null);

  const handleUpdateStart = (): void => {
    isUpdatingRef.current = true;
  };

  const handleUpdateEnd = (): void => {
    isUpdatingRef.current = false;
  };

  const resetColorPicker = (): void => {
    setResetKey(prev => prev + 1);
  };

  const handleColorChange = (
    color: string | number[],
    field: { onChange: (value: string) => void }
  ) => {
    // Prevent recursive updates during programmatic color changes
    if (isUpdatingRef.current) return;

    const hex = convertColorToHex(color, defaultColor);

    // Only trigger onChange if the color actually changed (case-insensitive)
    if (lastColorRef.current?.toLowerCase() === hex.toLowerCase()) {
      return;
    }
    lastColorRef.current = hex.toLowerCase();

    updateColor({
      color,
      defaultColor,
      onChange: field.onChange,
      onUpdateStart: handleUpdateStart,
      onUpdateEnd: handleUpdateEnd,
    });
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // Initialize lastColorRef with the current field value on first render
        if (lastColorRef.current === null) {
          lastColorRef.current = (field.value || defaultColor).toLowerCase();
        }

        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <div className="flex">
              <Popover
                open={disabled ? false : isOpen}
                onOpenChange={disabled ? undefined : setIsOpen}
              >
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      disabled={disabled}
                      className={cn(
                        'w-10 h-10 rounded-md p-1',
                        'disabled:border-input disabled:opacity-100 disabled:bg-muted disabled:pointer-events-none'
                      )}
                    >
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: field.value || defaultColor }}
                      />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" align="start">
                  <div className="space-y-4">
                    <ShadcnColorPicker
                      key={resetKey}
                      defaultValue={field.value || defaultColor}
                      onChange={color => handleColorChange(color, field)}
                      className="w-full"
                    >
                      <div className="mb-4 h-48 w-full">
                        <ColorPickerSelection />
                      </div>
                      <div className="mb-4">
                        <ColorPickerHue />
                      </div>
                    </ShadcnColorPicker>

                    <div className="grid grid-cols-9 gap-2">
                      {colors.map(color => (
                        <button
                          key={color}
                          type="button"
                          className="h-6 w-6 rounded border border-border transition-colors hover:border-muted-foreground"
                          style={{ backgroundColor: color }}
                          onClick={() => {
                            // Reset picker widget to sync with new preset color selection
                            resetColorPicker();
                            updateColor({
                              color,
                              defaultColor,
                              onChange: field.onChange,
                              onUpdateStart: handleUpdateStart,
                              onUpdateEnd: handleUpdateEnd,
                            });
                          }}
                        />
                      ))}
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        const randomColor = generateRandomColor();
                        resetColorPicker();
                        updateColor({
                          color: randomColor,
                          defaultColor,
                          onChange: field.onChange,
                          onUpdateStart: handleUpdateStart,
                          onUpdateEnd: handleUpdateEnd,
                        });
                      }}
                    >
                      {t('colorPicker.generateRandom')}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
